import { memo, useMemo, useCallback, useEffect } from 'react';
import dynamic from 'next/dynamic';

import { useTranslator } from '@picsart/rc/services/localization';

import { toSnakeCase, createPrefix } from '~/utils/Helpers';
import { IAdditionalInfoModel } from '~/models/additionalInfoModels';
import { ITopBarPropsModel } from '~/components/TopBar/TopBar';
import { getMorePosts, getCategoryPosts } from '~/utils/api';
import { ANALYTICS } from '~/utils/Constants';
import { IPostListItemModel } from '~/models/postModels';
import useAnalytics from '~/services/AnalyticsService';
import useDecodeString from '~/hooks/useDecodeString';

const { EVENT_NAME, SCREEN_NAMES, PAGE_TYPES } = ANALYTICS;

type CategoryProps = {
  categoryInfo: IAdditionalInfoModel;
  data: IPostListItemModel[];
  pagesCount: number;
  slug: string;
};

const TaxonomyTemplate = dynamic(
  () => import('~/Templates/TaxonomyTemplate/TaxonomyTemplate'),
);

function Category({ categoryInfo, pagesCount, data, slug }: CategoryProps) {
  const translate = useTranslator();
  const { sendEvent } = useAnalytics();

  useEffect(() => {
    sendEvent(EVENT_NAME.pageOpen, {
      screen_name: SCREEN_NAMES.blog,
      page_type: PAGE_TYPES.category,
      page_name: toSnakeCase(categoryInfo.name),
    });
  }, [sendEvent, categoryInfo.name]);

  const analyticsPageClick = useCallback(
    (value, action, position) => {
      sendEvent(EVENT_NAME.pageClick, {
        screen_name: SCREEN_NAMES.blog,
        button_name: toSnakeCase(value),
        page_type: PAGE_TYPES.category,
        action_button: action,
        page_name: toSnakeCase(categoryInfo.slug),
        ...(position && { position }),
      });
    },
    [sendEvent, categoryInfo],
  );

  const topBarProps = useMemo<ITopBarPropsModel>(
    () => ({
      header: translate(categoryInfo.name),
      subHeader: translate('category'),
      watermark: translate('Search'),
    }),
    [categoryInfo, translate],
  );

  const metaDescription = useDecodeString(
    data[0]?.excerpt.rendered
      ? data[0].excerpt.rendered.replace(/(<([^>]+)>)/gi, '')
      : '',
  );

  return (
    <>
      <TaxonomyTemplate
        analyticsEvent={analyticsPageClick}
        initialPagesCount={pagesCount}
        meta={categoryInfo.yoast_head}
        description={metaDescription}
        topBarProps={topBarProps}
        loadMore={getMorePosts}
        initialPosts={data}
        query={slug}
      />
    </>
  );
}

export async function getServerSideProps({ params, req, locale }) {
  const pathPrefix = createPrefix(req, locale);

  const {
    data = [],
    pagesCount,
    categoryInfo,
  } = await getCategoryPosts(encodeURIComponent(params.slug), pathPrefix);
  if (!categoryInfo) {
    return {
      notFound: true,
    };
  }
  return {
    props: {
      pagesCount: parseInt(pagesCount, 10),
      slug: `categories=${categoryInfo.id}`,
      categoryInfo,
      data,
    },
  };
}

export default memo<CategoryProps>(Category);
